import { Button, Divider, Heading, MenuTrigger, Stack, Text } from '@eleveo/components-react';
import { BinIcon, DuplicatePlusIcon, PencilIcon, ThreeDotsIcon } from '@eleveo/icons-react';
import React, { useState } from 'react';
import { FormProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { IntradayQueuesToOptions, NotificationForm, NotificationFormType } from './NotificationForm';
import { getNotificationActionTag, getViewFieldsTag } from './constants';
import { useAuth } from '../../app/AuthorizationProvider';
import { IntradayQueueNotification } from '../../graphql/components';

type NotificationTileProps = {
  handleDelete: (queueId: string) => void;
  handleDuplicate: (notificationToDuplicate: IntradayQueueNotification) => void;
  hasEditPermission: boolean;
  intradayQueueNotification: IntradayQueueNotification;
  onSubmit: (callBack: () => void) => FormProps<NotificationFormType>['onSubmit'];
  queuesList: IntradayQueuesToOptions;
  title: string;
};

export const NotificationTile: React.FC<NotificationTileProps> = ({
  handleDelete,
  handleDuplicate,
  hasEditPermission,
  intradayQueueNotification,
  onSubmit,
  queuesList,
  title,
}) => {
  const { locale } = useAuth();
  const { t } = useTranslation();

  const [editMode, setEditMode] = useState(false);

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  const isContactVolumeDeviationDefined = typeof intradayQueueNotification.contactVolumeDeviation === 'number';
  const isSlaDefined =
    typeof (intradayQueueNotification.slaOutOfRangeFrom || intradayQueueNotification.slaOutOfRangeTo) === 'number';

  if (editMode) {
    return (
      <NotificationForm
        handleCancel={handleCancelEdit}
        intradayQueueNotification={intradayQueueNotification}
        onSubmit={onSubmit(handleCancelEdit)}
        queuesList={queuesList}
      />
    );
  }

  return (
    <Stack direction="vertical" className="w-full p-20">
      <div className="flex justify-between pb-10">
        <Heading as="h3" className="text-xl text-blue-700">
          {title}
        </Heading>
        <MenuTrigger
          items={[
            {
              icon: <PencilIcon />,
              id: '1',
              onClick: () => setEditMode(true),
              testId: getNotificationActionTag('edit'),
              text: t('layout.action.edit'),
            },
            {
              icon: <DuplicatePlusIcon />,
              id: '2',
              onClick: () => handleDuplicate(intradayQueueNotification),
              testId: getNotificationActionTag('duplicate'),
              text: t('layout.action.duplicate'),
            },
            {
              id: '3',
              options: [
                {
                  icon: <BinIcon />,
                  id: '1',
                  onClick: () => handleDelete(intradayQueueNotification.queueId),
                  testId: getNotificationActionTag('delete'),
                  text: t('layout.action.delete'),
                },
              ],
            },
          ]}
        >
          {hasEditPermission && (
            <Button
              aria-label={t('layout.action.actions')}
              isSmall
              testId={getNotificationActionTag('actions')}
              variant="quiet"
            >
              <ThreeDotsIcon />
            </Button>
          )}
        </MenuTrigger>
      </div>

      {isContactVolumeDeviationDefined && (
        <>
          <Text weight="bold">{t('intraday.charts.contactVolumes')}</Text>
          <Stack spacing={20}>
            <Text>{t('intraday.deviation')}:</Text>
            <Text weight="bold" testId={getViewFieldsTag('contactVolumes')}>
              {intradayQueueNotification.contactVolumeDeviation?.toLocaleString(locale, {
                style: 'percent',
              })}
            </Text>
          </Stack>
        </>
      )}

      {isContactVolumeDeviationDefined && isSlaDefined && <Divider className="my-10" />}

      {isSlaDefined && (
        <>
          <Text weight="bold">{t('intraday.charts.serviceLevelAgreement')}</Text>
          <Stack spacing={20}>
            <Text>{t('intraday.outOfRange')}:</Text>
            <Text weight="bold" testId={getViewFieldsTag('serviceLevelAgreement')}>
              {intradayQueueNotification.slaOutOfRangeFrom?.toLocaleString(locale, {
                style: 'percent',
              })}{' '}
              -{' '}
              {intradayQueueNotification.slaOutOfRangeTo?.toLocaleString(locale, {
                style: 'percent',
              })}
            </Text>
          </Stack>
        </>
      )}
    </Stack>
  );
};
